/**
 * Comprehensive validation utilities for generation request parameters.
 * 
 * This module provides validation functions and error handling for generation
 * requests, ensuring that courseId and key parameters meet requirements before
 * sending requests to the unified generation API.
 * 
 * @example
 * ```typescript
 * import { validateGenerationParams, isValidGenerationRequest } from './generationValidation';
 * 
 * // Comprehensive validation with detailed errors
 * const result = validateGenerationParams(123, "lesson-intro");
 * if (!result.isValid) {
 *   console.log(result.errors); // Detailed error messages
 * }
 * 
 * // Simple boolean check
 * if (isValidGenerationRequest({ courseId: 123, key: "lesson-intro" })) {
 *   // Proceed with generation
 * }
 * ```
 *//**
 * Validation result interface containing validation status and error details.
 *//**
 * Error types for different validation failures.
 */export let ValidationErrorType=/*#__PURE__*/function(ValidationErrorType){ValidationErrorType["MISSING_COURSE_ID"]="MISSING_COURSE_ID";ValidationErrorType["INVALID_COURSE_ID"]="INVALID_COURSE_ID";ValidationErrorType["MISSING_KEY"]="MISSING_KEY";ValidationErrorType["INVALID_KEY"]="INVALID_KEY";ValidationErrorType["MULTIPLE_ERRORS"]="MULTIPLE_ERRORS";return ValidationErrorType;}({});/**
 * Structured error class for generation validation failures.
 * Extends the standard Error class with additional validation context.
 */export class GenerationValidationError extends Error{/**
   * Creates a new GenerationValidationError instance.
   * 
   * @param message - Primary error message
   * @param errorType - Specific type of validation error
   * @param validationResult - Complete validation result with all errors
   * 
   * @example
   * ```typescript
   * const error = new GenerationValidationError(
   *   "Invalid course ID",
   *   ValidationErrorType.INVALID_COURSE_ID,
   *   validationResult
   * );
   * ```
   */constructor(message,errorType,validationResult){super(message);this.errorType=void 0;this.fieldErrors=void 0;this.validationResult=void 0;this.name='GenerationValidationError';this.errorType=errorType;this.fieldErrors=validationResult.fieldErrors;this.validationResult=validationResult;}/**
   * Get all validation errors as a formatted string.
   * 
   * @returns Formatted string containing all validation errors
   */getAllErrors(){return this.validationResult.errors.join('; ');}/**
   * Get field-specific errors for a given field.
   * 
   * @param field - The field name to get errors for
   * @returns Array of error messages for the specified field
   */getFieldErrors(field){return this.fieldErrors[field]||[];}}/**
 * Validates that a courseId is a positive integer.
 * 
 * @param courseId - The courseId value to validate
 * @returns True if courseId is a positive integer, false otherwise
 * 
 * @example
 * ```typescript
 * validateCourseId(123);     // true
 * validateCourseId("123");   // true
 * validateCourseId(-1);      // false
 * validateCourseId("abc");   // false
 * validateCourseId(null);    // false
 * ```
 */export function validateCourseId(courseId){if(courseId===null||courseId===undefined){return false;}// Convert to number if it's a string
const numericValue=typeof courseId==='string'?parseInt(courseId,10):courseId;// Check if it's a valid positive integer
return typeof numericValue==='number'&&!isNaN(numericValue)&&Number.isInteger(numericValue)&&numericValue>0;}/**
 * Validates that a key is a non-empty string.
 * 
 * @param key - The key value to validate
 * @returns True if key is a non-empty string, false otherwise
 * 
 * @example
 * ```typescript
 * validateNodeKey("lesson-intro");  // true
 * validateNodeKey("quiz_1");        // true
 * validateNodeKey("");              // false
 * validateNodeKey("   ");           // false (whitespace only)
 * validateNodeKey(null);            // false
 * validateNodeKey(123);             // false
 * ```
 */export function validateNodeKey(key){return typeof key==='string'&&key.trim().length>0;}/**
 * Sanitizes and normalizes a courseId parameter.
 * 
 * @param courseId - The courseId to sanitize
 * @returns Normalized courseId as a positive integer, or null if invalid
 * 
 * @example
 * ```typescript
 * sanitizeCourseId("123");   // 123
 * sanitizeCourseId(" 456 "); // 456
 * sanitizeCourseId("abc");   // null
 * sanitizeCourseId(-1);      // null
 * ```
 */export function sanitizeCourseId(courseId){if(courseId===null||courseId===undefined){return null;}const trimmed=typeof courseId==='string'?courseId.trim():courseId;const numericValue=typeof trimmed==='string'?parseInt(trimmed,10):trimmed;if(typeof numericValue==='number'&&!isNaN(numericValue)&&Number.isInteger(numericValue)&&numericValue>0){return numericValue;}return null;}/**
 * Sanitizes and normalizes a key parameter.
 * 
 * @param key - The key to sanitize
 * @returns Normalized key as a trimmed string, or null if invalid
 * 
 * @example
 * ```typescript
 * sanitizeNodeKey("  lesson-intro  "); // "lesson-intro"
 * sanitizeNodeKey("quiz_1");           // "quiz_1"
 * sanitizeNodeKey("");                 // null
 * sanitizeNodeKey(123);                // null
 * ```
 */export function sanitizeNodeKey(key){if(typeof key!=='string'){return null;}const trimmed=key.trim();return trimmed.length>0?trimmed:null;}/**
 * Performs comprehensive validation of generation parameters with detailed error messages.
 * 
 * @param courseId - The courseId to validate
 * @param key - The key to validate
 * @returns ValidationResult with detailed error information
 * 
 * @example
 * ```typescript
 * // Valid parameters
 * const result1 = validateGenerationParams(123, "lesson-intro");
 * console.log(result1.isValid); // true
 * 
 * // Invalid parameters
 * const result2 = validateGenerationParams(null, "");
 * console.log(result2.errors); 
 * // ["Missing courseId. Expected a positive integer (e.g., 1, 42, 123)",
 * //  "Missing or empty key. Expected a non-empty string (e.g., 'lesson-intro', 'quiz_1')"]
 * ```
 */export function validateGenerationParams(courseId,key){const errors=[];const fieldErrors={};// Validate courseId
if(courseId===null||courseId===undefined){const error="Missing courseId. Expected a positive integer (e.g., 1, 42, 123)";errors.push(error);fieldErrors.courseId=[error];}else if(!validateCourseId(courseId)){const error=`Invalid courseId "${courseId}". Expected a positive integer (e.g., 1, 42, 123)`;errors.push(error);fieldErrors.courseId=[error];}// Validate key
if(key===null||key===undefined){const error="Missing key. Expected a non-empty string (e.g., 'lesson-intro', 'quiz_1', 'exercise-2')";errors.push(error);fieldErrors.key=[error];}else if(!validateNodeKey(key)){if(typeof key==='string'&&key.trim().length===0){const error="Empty key provided. Expected a non-empty string (e.g., 'lesson-intro', 'quiz_1', 'exercise-2')";errors.push(error);fieldErrors.key=[error];}else{const error=`Invalid key "${key}". Expected a non-empty string (e.g., 'lesson-intro', 'quiz_1', 'exercise-2')`;errors.push(error);fieldErrors.key=[error];}}return{isValid:errors.length===0,errors,fieldErrors};}/**
 * Generates user-friendly error messages from a GenerationValidationError.
 * 
 * @param error - The GenerationValidationError to format
 * @returns User-friendly error message string
 * 
 * @example
 * ```typescript
 * const result = validateGenerationParams(null, "");
 * const error = new GenerationValidationError(
 *   "Validation failed",
 *   ValidationErrorType.MULTIPLE_ERRORS,
 *   result
 * );
 * 
 * const message = getValidationErrorMessage(error);
 * console.log(message); // "Validation failed: Missing courseId...; Missing key..."
 * ```
 */export function getValidationErrorMessage(error){const baseMessage=error.message;const allErrors=error.getAllErrors();if(allErrors){return`${baseMessage}: ${allErrors}`;}return baseMessage;}/**
 * Simple boolean check for generation request validity.
 * 
 * @param args - Object containing courseId and key parameters
 * @returns True if both parameters are valid, false otherwise
 * 
 * @example
 * ```typescript
 * // Valid request
 * isValidGenerationRequest({ courseId: 123, key: "lesson-intro" }); // true
 * 
 * // Invalid requests
 * isValidGenerationRequest({ courseId: null, key: "lesson-intro" }); // false
 * isValidGenerationRequest({ courseId: 123, key: "" }); // false
 * isValidGenerationRequest({}); // false
 * ```
 */export function isValidGenerationRequest(args){const{courseId,key}=args;return validateCourseId(courseId)&&validateNodeKey(key);}/**
 * Creates a GenerationValidationError from validation results.
 * 
 * @param courseId - The courseId that was validated
 * @param key - The key that was validated
 * @returns GenerationValidationError if validation fails, null if validation passes
 * 
 * @example
 * ```typescript
 * const error = createValidationError(null, "");
 * if (error) {
 *   throw error; // Throws GenerationValidationError with detailed information
 * }
 * ```
 */export function createValidationError(courseId,key){const result=validateGenerationParams(courseId,key);if(result.isValid){return null;}// Determine primary error type
let errorType;const hasCourseIdError=result.fieldErrors.courseId&&result.fieldErrors.courseId.length>0;const hasKeyError=result.fieldErrors.key&&result.fieldErrors.key.length>0;if(hasCourseIdError&&hasKeyError){errorType=ValidationErrorType.MULTIPLE_ERRORS;}else if(hasCourseIdError){if(courseId===null||courseId===undefined){errorType=ValidationErrorType.MISSING_COURSE_ID;}else{errorType=ValidationErrorType.INVALID_COURSE_ID;}}else if(hasKeyError){if(key===null||key===undefined){errorType=ValidationErrorType.MISSING_KEY;}else{errorType=ValidationErrorType.INVALID_KEY;}}else{errorType=ValidationErrorType.MULTIPLE_ERRORS;}const message=result.errors.length===1?result.errors[0]:'Validation failed';return new GenerationValidationError(message,errorType,result);}/**
 * Validates and sanitizes generation request parameters in one operation.
 * 
 * @param args - Object containing courseId and key parameters
 * @returns Object with sanitized values and validation status
 * 
 * @example
 * ```typescript
 * const result = validateAndSanitizeRequest({ courseId: "123", key: "  lesson-intro  " });
 * if (result.isValid) {
 *   console.log(result.sanitized.courseId); // 123
 *   console.log(result.sanitized.key);      // "lesson-intro"
 * } else {
 *   console.log(result.error.getAllErrors());
 * }
 * ```
 */export function validateAndSanitizeRequest(args){const{courseId,key}=args;const sanitizedCourseId=sanitizeCourseId(courseId);const sanitizedKey=sanitizeNodeKey(key);const error=createValidationError(courseId,key);return{isValid:error===null,sanitized:{courseId:sanitizedCourseId,key:sanitizedKey},error};}/**
 * Type guard to check if an error is a GenerationValidationError.
 * 
 * @param error - The error to check
 * @returns True if error is a GenerationValidationError
 * 
 * @example
 * ```typescript
 * try {
 *   // Some operation that might throw
 * } catch (error) {
 *   if (isGenerationValidationError(error)) {
 *     console.log(error.errorType);
 *     console.log(error.fieldErrors);
 *   }
 * }
 * ```
 */export function isGenerationValidationError(error){return error instanceof GenerationValidationError;}// All types and functions are already exported above